import React, { useState, useEffect, useRef, useContext } from 'react';
import { AuthContext } from '../../components/AuthContext';
import Swal from 'sweetalert2';
import {
  FaPlus,
  FaSearch,
  FaTimes,
  FaEdit,
  FaTrash,
  FaCog,
  FaTags,
  FaCheckCircle,
  FaExclamationCircle
} from 'react-icons/fa';
import useDraftManager from '../../hooks/useDraftManager';

const TilesParameters = () => {
  const { user } = useContext(AuthContext);
  const [parameters, setParameters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalParameters, setTotalParameters] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentParameter, setCurrentParameter] = useState(null);
  const [formData, setFormData] = useState({
    parameterName: '',
    assignedFor: 'category',
    createdBy: user.user._id
  });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [filterAssignedFor, setFilterAssignedFor] = useState('');
  const [sortBy, setSortBy] = useState('parameterName');
  const [sortOrder, setSortOrder] = useState('asc');
  
  // Notification states for draft auto-save
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  
  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode === 'true';
  });

  // Dark mode sync with localStorage
  useEffect(() => {
    const interval = setInterval(() => {
      const savedMode = localStorage.getItem('darkMode');
      setDarkMode(savedMode === 'true');
    }, 500);
    return () => clearInterval(interval);
  }, []);
  
  // Enhanced pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  
  // Reference for focusing input field when modal opens
  const parameterNameInputRef = useRef(null);
  
  // Track if search is active
  const isSearchActive = searchQuery.trim().length > 0;

  // Draft Manager
  const draftManager = useDraftManager({
    featureType: 'tilesParametersManagement',
    formData: !currentParameter && formData.parameterName.trim() ? formData : null, // Only save draft if user has entered a name
    setFormData: setFormData,
    userId: user?.user?._id,
    companyId: user?.user?.company,
    onDraftLoaded: (draft) => {
      console.log('Tiles Parameters draft loaded:', draft);
      // Auto-open the modal when draft is loaded
      setShowAddModal(true);
      Swal.fire({
        icon: 'success',
        title: 'Draft Loaded',
        text: 'Your previous draft has been loaded',
        timer: 2000,
        showConfirmButton: false
      });
    },
    onDraftSaved: (draft, isManual) => {
      setNotificationMessage('Draft saved automatically');
      setShowNotification(true);
    },
    onError: (error) => {
      console.error('Tiles Parameters draft error:', error);
    }
  });

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchParameters();
    }, 300); // Debounce search
    return () => clearTimeout(timeoutId);
  }, [currentPage, pageSize, searchQuery, filterAssignedFor, sortBy, sortOrder]);

  // Focus input when modal opens
  useEffect(() => {
    if (showAddModal || showEditModal) {
      setTimeout(() => {
        parameterNameInputRef.current?.focus();
      }, 100);
    }
  }, [showAddModal, showEditModal]);

  const fetchParameters = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
        filter: searchQuery,
        ...(filterAssignedFor && { assignedFor: filterAssignedFor }),
        sortBy,
        sortOrder
      });

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tiles/parameters?${queryParams}`);
      const result = await response.json();

      if (result.success) {
        setParameters(result.data.parameters || result.parameters || []);
        
        // Handle enhanced pagination data
        const paginationData = result.data.pagination || {
          currentPage: result.currentPage || currentPage,
          totalPages: result.totalPages || 0,
          totalItems: result.total || 0,
          hasNextPage: (result.currentPage || currentPage) < (result.totalPages || 0),
          hasPrevPage: (result.currentPage || currentPage) > 1
        };
        
        setPagination(paginationData);
        setTotalParameters(paginationData.totalItems);
        
        console.log('Parameters fetched:', {
          count: result.data.parameters?.length || result.parameters?.length || 0,
          pagination: paginationData
        });
      } else {
        throw new Error(result.message || 'Failed to fetch parameters');
      }
    } catch (error) {
      console.error('Error fetching parameters:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch parameters'
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const resetSearch = () => {
    setSearchQuery('');
    setFilterAssignedFor('');
    setSortBy('parameterName');
    setSortOrder('asc');
    setCurrentPage(1);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const openAddModal = () => {
    setFormData({
      parameterName: '',
      assignedFor: 'category',
      createdBy: user.user._id
    });
    setCurrentParameter(null);
    setErrors({});
    setShowAddModal(true);
  };

  const openEditModal = async (parameter) => {
    // Delete any existing draft when editing a parameter
    if (draftManager.currentDraftId) {
      try {
        await draftManager.deleteDraft(draftManager.currentDraftId);
        console.log('✅ Draft deleted when editing tiles parameter');
      } catch (draftError) {
        console.error('⚠️ Failed to delete draft on edit:', draftError);
        // Don't block user flow if draft deletion fails
      }
    }
    
    setFormData({
      parameterName: parameter.parameterName,
      assignedFor: parameter.assignedFor
    });
    setCurrentParameter(parameter);
    setErrors({});
    setShowEditModal(true);
  };

  // Check for duplicate parameter before submission
  const checkDuplicateParameter = async (parameterName, assignedFor, id = null) => {
    try {
      const existingParams = parameters.filter(param => 
        param.parameterName.toLowerCase() === parameterName.toLowerCase() &&
        param.assignedFor === assignedFor &&
        param._id !== id
      );
      
      return existingParams.length > 0;
    } catch (error) {
      console.error('Error checking duplicate:', error);
      return false;
    }
  };

  const validateForm = async (isEdit = false) => {
    const newErrors = {};
    
    if (!formData.parameterName.trim()) {
      newErrors.parameterName = 'Parameter name is required';
    }
    
    if (!formData.assignedFor) {
      newErrors.assignedFor = 'Assigned for is required';
    }
    
    // Check for duplicates
    if (formData.parameterName.trim() && formData.assignedFor) {
      const isDuplicate = await checkDuplicateParameter(
        formData.parameterName.trim(),
        formData.assignedFor,
        isEdit ? currentParameter._id : null
      );
      
      if (isDuplicate) {
        newErrors.parameterName = `Parameter "${formData.parameterName}" already exists for ${formData.assignedFor}`;
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const createParameter = async (e) => {
    e.preventDefault();
    
    if (!(await validateForm())) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tiles/parameters`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          parameterName: formData.parameterName.trim(),
          assignedFor: formData.assignedFor,
          createdBy: user.user._id
        }),
      });

      const result = await response.json();
      
      if (result.error) {
        if (result.type === 'DUPLICATE_ERROR') {
          setErrors({ parameterName: result.error });
        } else {
          throw new Error(result.error);
        }
        return;
      }

      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Parameter created successfully',
        timer: 2000,
        showConfirmButton: false
      });

      // Delete draft after successful creation
      if (draftManager.currentDraftId) {
        try {
          await draftManager.deleteDraft(draftManager.currentDraftId);
          console.log('✅ Draft deleted after parameter creation');
        } catch (draftError) {
          console.error('⚠️ Failed to delete draft:', draftError);
        }
      }

      setShowAddModal(false);
      fetchParameters();
      
    } catch (error) {
      console.error('Error creating parameter:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to create parameter'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const updateParameter = async (e) => {
    e.preventDefault();
    
    if (!(await validateForm(true))) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tiles/parameters/${currentParameter._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          parameterName: formData.parameterName.trim(),
          assignedFor: formData.assignedFor
        }),
      });

      const result = await response.json();
      
      if (result.error) {
        if (result.type === 'DUPLICATE_ERROR') {
          setErrors({ parameterName: result.error });
        } else {
          throw new Error(result.error);
        }
        return;
      }

      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Parameter updated successfully',
        timer: 2000,
        showConfirmButton: false
      });

      // Delete draft after successful update
      if (draftManager.currentDraftId) {
        try {
          await draftManager.deleteDraft(draftManager.currentDraftId);
          console.log('✅ Draft deleted after parameter update');
        } catch (draftError) {
          console.error('⚠️ Failed to delete draft:', draftError);
        }
      }

      setShowEditModal(false);
      fetchParameters();
      
    } catch (error) {
      console.error('Error updating parameter:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to update parameter'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (parameterId) => {
    try {
      // First check if parameter is in use
      const checkResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tiles/check-parameter-usage/${parameterId}`);
      const checkResult = await checkResponse.json();
      
      if (checkResult.isInUse) {
        Swal.fire({
          icon: 'warning',
          title: 'Cannot Delete',
          text: 'This parameter is currently in use and cannot be deleted.'
        });
        return;
      }

      const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'This parameter will be permanently deleted.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!'
      });

      if (result.isConfirmed) {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tiles/parameters/${parameterId}`, {
          method: 'DELETE'
        });

        const deleteResult = await response.json();
        
        if (deleteResult.success) {
          Swal.fire({
            icon: 'success',
            title: 'Deleted!',
            text: 'Parameter has been deleted.',
            timer: 2000,
            showConfirmButton: false
          });
          fetchParameters();
        } else {
          throw new Error(deleteResult.message || 'Failed to delete parameter');
        }
      }
    } catch (error) {
      console.error('Error deleting parameter:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to delete parameter'
      });
    }
  };

  // Enhanced pagination helpers
  const getPageNumbers = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, currentPage - delta); i <= Math.min(pagination.totalPages - 1, currentPage + delta); i++) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < pagination.totalPages - 1) {
      rangeWithDots.push('...', pagination.totalPages);
    } else if (pagination.totalPages > 1) {
      rangeWithDots.push(pagination.totalPages);
    }

    return rangeWithDots;
  };
  
  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;

    return (
      <div className={`px-4 sm:px-6 py-3 sm:py-4 border-t ${
        darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm">
            <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <span className="hidden sm:inline">Showing </span>
              <span className="font-medium">{((currentPage - 1) * pageSize) + 1}</span>
              <span className="hidden sm:inline"> to </span>
              <span className="sm:hidden">-</span>
              <span className="font-medium">{Math.min(currentPage * pageSize, totalParameters)}</span>
              <span className="hidden sm:inline"> of </span>
              <span className="sm:hidden">/</span>
              <span className="font-medium">{totalParameters}</span>
              <span className="hidden sm:inline"> results</span>
            </p>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(parseInt(e.target.value));
                setCurrentPage(1);
              }}
              className={`px-2 sm:px-3 py-1 border rounded-lg text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 transition-all ${
                darkMode 
                  ? 'bg-gray-700 border-gray-600 text-gray-200' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
          
          <nav className="flex items-center justify-center sm:justify-start space-x-1">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={!pagination.hasPrevPage}
              className={`relative inline-flex items-center px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium border rounded-md transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                darkMode 
                  ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' 
                  : 'text-gray-500 bg-white border-gray-300 hover:bg-gray-50'
              }`}
            >
              <span className="hidden sm:inline">Previous</span>
              <span className="sm:hidden">Prev</span>
            </button>
            
            <div className="hidden sm:flex items-center space-x-1">
              {getPageNumbers().map((pageNum, index) => (
                pageNum === '...' ? (
                  <span key={index} className={`px-3 py-1 text-sm ${
                    darkMode ? 'text-gray-500' : 'text-gray-500'
                  }`}>...</span>
                ) : (
                  <button
                    key={index}
                    onClick={() => handlePageChange(pageNum)}
                    className={`relative inline-flex items-center px-3 py-1 text-sm font-medium border rounded-md transition-all ${
                      pageNum === currentPage
                        ? darkMode 
                          ? 'z-10 bg-blue-500/20 border-blue-500 text-blue-400' 
                          : 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                        : darkMode 
                          ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600' 
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                )
              ))}
            </div>
            
            {/* Mobile: Show only current page */}
            <div className="sm:hidden flex items-center">
              <span className={`px-2 py-1 text-xs font-medium ${
                darkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                {currentPage} / {pagination.totalPages}
              </span>
            </div>
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!pagination.hasNextPage}
              className={`relative inline-flex items-center px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium border rounded-md transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                darkMode 
                  ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' 
                  : 'text-gray-500 bg-white border-gray-300 hover:bg-gray-50'
              }`}
            >
              Next
            </button>
          </nav>
        </div>
      </div>
    );
  };
  
  const getAssignedForLabel = (value) => {
    const labels = {
      inches: 'Inches',
      mm: 'MM',
      category: 'Category',
      type: 'Type',
      finish: 'Finish'
    };
    return labels[value] || value;
  };

  // Function to handle keyboard events in modals
  const handleModalKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowAddModal(false);
      setShowEditModal(false);
    }
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      darkMode 
        ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' 
        : 'bg-gradient-to-br from-blue-50 via-purple-50 to-indigo-50'
    }`}>
      {/* Modern Header with Gradient */}
      <div className={`shadow-2xl border-b ${
        darkMode 
          ? 'bg-gradient-to-r from-gray-800 to-gray-900 border-gray-700' 
          : 'bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 border-transparent'
      }`}>
        <div className="px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-start sm:items-center gap-3 sm:gap-4 mb-3">
                <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-xl transform hover:scale-110 transition-transform duration-300 ${
                  darkMode ? 'bg-purple-500' : 'bg-white/20 backdrop-blur-sm'
                }`}>
                  <FaCog className="text-3xl sm:text-4xl text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
                    Tiles Parameters
                  </h1>
                  <div className="flex items-center gap-2 mt-2">
                    <div className={`w-2 h-2 rounded-full ${
                      darkMode ? 'bg-green-400' : 'bg-green-300'
                    } animate-pulse`}></div>
                    <p className={`text-sm sm:text-base ${
                      darkMode ? 'text-gray-300' : 'text-blue-100'
                    }`}>
                      Manage Categories, Brands & Specifications
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className={`flex flex-col gap-3 px-6 py-4 rounded-2xl shadow-lg backdrop-blur-sm ${
              darkMode ? 'bg-gray-800/50' : 'bg-white/20'
            }`}>
              <div className="flex items-center gap-3">
                <FaTags className="text-white text-2xl" />
                <div>
                  <p className={`text-xs ${
                    darkMode ? 'text-gray-300' : 'text-blue-100'
                  }`}>
                    Total Parameters
                  </p>
                  <p className="font-bold text-2xl text-white">{totalParameters}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <div className={`rounded-2xl shadow-xl p-6 mb-6 border-2 ${
          darkMode 
            ? 'bg-gray-800 border-gray-700' 
            : 'bg-white border-gray-100'
        }`}>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            {/* Search */}
            <div className="flex-1">
              <label className={`block text-sm font-semibold mb-2 ${
                darkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                🔍 Search Parameters
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Search by parameter name..."
                  className={`w-full pl-12 pr-12 py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400 focus:border-blue-500' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                  }`}
                />
                <span className={`absolute left-4 top-3.5 ${
                  darkMode ? 'text-gray-500' : 'text-gray-400'
                }`}>
                  <FaSearch className="h-5 w-5" />
                </span>
                {isSearchActive && (
                  <button
                    onClick={resetSearch}
                    className={`absolute right-4 top-3.5 transition-colors ${
                      darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'
                    }`}
                  >
                    <FaTimes className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>

            {/* Filter and Actions */}
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Filter */}
              <div className="flex-1 sm:w-48">
                <label className={`block text-sm font-semibold mb-2 ${
                  darkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  📂 Filter by Type
                </label>
                <select
                  value={filterAssignedFor}
                  onChange={(e) => {
                    setFilterAssignedFor(e.target.value);
                    setCurrentPage(1);
                  }}
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600 text-gray-200 focus:border-blue-500' 
                      : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                  }`}
                >
                  <option value="">All Types</option>
                  <option value="category">Category</option>
                  <option value="brand">Brand</option>
                  <option value="type">Type</option>
                  <option value="finish">Finish</option>
                  <option value="size">Size</option>
                </select>
              </div>

              {/* Add Button */}
              <div className="flex items-end">
                <button
                  onClick={openAddModal}
                  className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-3 rounded-xl transition-all flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-105 font-semibold"
                >
                  <FaPlus className="mr-2" />
                  Add Parameter
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Parameters Table */}
      <div className={`rounded-2xl shadow-xl overflow-hidden ${
        darkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        {loading ? (
          <div className="flex justify-center items-center h-60">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
              <p className={`mt-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Loading parameters...
              </p>
            </div>
          </div>
        ) : parameters.length > 0 ? (
          <>
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                  <tr>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      darkMode ? 'text-gray-300' : 'text-gray-500'
                    }`}>ID</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      darkMode ? 'text-gray-300' : 'text-gray-500'
                    }`}>Parameter Name</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      darkMode ? 'text-gray-300' : 'text-gray-500'
                    }`}>Assigned For</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      darkMode ? 'text-gray-300' : 'text-gray-500'
                    }`}>Actions</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${
                  darkMode ? 'divide-gray-700 bg-gray-800' : 'divide-gray-200 bg-white'
                }`}>
                  {parameters.map((param) => (
                    <tr key={param._id} className={`transition-colors ${
                      darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                    }`}>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${
                        darkMode ? 'text-gray-200' : 'text-gray-900'
                      }`}>{param.id}</td>
                      <td className={`px-6 py-4 text-sm ${
                        darkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>{param.parameterName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          darkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {getAssignedForLabel(param.assignedFor)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openEditModal(param)}
                            className={`group relative px-4 py-2 rounded-lg font-medium transition-all duration-200 transform hover:scale-105 flex items-center gap-2 shadow-md ${
                              darkMode 
                                ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white shadow-blue-500/30' 
                                : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-blue-500/50'
                            }`}
                          >
                            <FaEdit className="h-3.5 w-3.5" />
                            <span className="text-xs font-bold">Edit</span>
                          </button>
                          <button
                            onClick={() => handleDelete(param._id)}
                            className={`group relative px-4 py-2 rounded-lg font-medium transition-all duration-200 transform hover:scale-105 flex items-center gap-2 shadow-md ${
                              darkMode 
                                ? 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white shadow-red-500/30' 
                                : 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white shadow-red-500/50'
                            }`}
                          >
                            <FaTrash className="h-3.5 w-3.5" />
                            <span className="text-xs font-bold">Delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-4 p-4">
              {parameters.map((param) => (
                <div
                  key={param._id}
                  className={`rounded-xl p-4 shadow-lg border transition-all ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600' 
                      : 'bg-white border-gray-200'
                  }`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className={`text-xs font-medium mb-1 ${
                        darkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>ID</p>
                      <p className={`text-sm font-bold ${
                        darkMode ? 'text-gray-200' : 'text-gray-900'
                      }`}>{param.id}</p>
                    </div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      darkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {getAssignedForLabel(param.assignedFor)}
                    </span>
                  </div>

                  <div className="mb-4">
                    <p className={`text-xs font-medium mb-1 ${
                      darkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>Parameter Name</p>
                    <p className={`text-sm font-medium ${
                      darkMode ? 'text-gray-200' : 'text-gray-900'
                    }`}>{param.parameterName}</p>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => openEditModal(param)}
                      className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-all duration-200 transform active:scale-95 flex items-center justify-center gap-2 shadow-md ${
                        darkMode 
                          ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white shadow-blue-500/30' 
                          : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-blue-500/50'
                      }`}
                    >
                      <FaEdit className="h-4 w-4" />
                      <span className="text-sm font-bold">Edit</span>
                    </button>
                    <button
                      onClick={() => handleDelete(param._id)}
                      className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-all duration-200 transform active:scale-95 flex items-center justify-center gap-2 shadow-md ${
                        darkMode 
                          ? 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white shadow-red-500/30' 
                          : 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white shadow-red-500/50'
                      }`}
                    >
                      <FaTrash className="h-4 w-4" />
                      <span className="text-sm font-bold">Delete</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {renderPagination()}
          </>
        ) : (
          <div className="p-8 text-center">
            <FaExclamationCircle className={`mx-auto h-12 w-12 ${
              darkMode ? 'text-gray-600' : 'text-gray-400'
            }`} />
            <h3 className={`mt-2 text-sm font-medium ${
              darkMode ? 'text-gray-200' : 'text-gray-900'
            }`}>No parameters found</h3>
            <p className={`mt-1 text-sm ${
              darkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              {isSearchActive 
                ? `No parameters match your search "${searchQuery}"`
                : "Get started by adding a new parameter"
              }
            </p>
            {isSearchActive ? (
              <button
                onClick={resetSearch}
                className={`mt-4 text-sm transition-colors ${
                  darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
                }`}
              >
                Clear search filter
              </button>
            ) : (
              <button
                onClick={openAddModal}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <FaPlus className="-ml-1 mr-2 h-5 w-5" />
                Add Parameter
              </button>
            )}
          </div>
        )}
      </div>

      {/* Add Parameter Modal */}
      {showAddModal && (
        <div 
          className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 p-4"
          onKeyDown={handleModalKeyDown}
        >
          <div className={`p-6 rounded-2xl shadow-2xl w-full max-w-md animate-fade-in-down ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  darkMode ? 'bg-blue-500/20' : 'bg-blue-50'
                }`}>
                  <FaPlus className={`text-xl ${
                    darkMode ? 'text-blue-400' : 'text-blue-600'
                  }`} />
                </div>
                <h2 className={`text-lg font-bold ${
                  darkMode ? 'text-white' : 'text-gray-900'
                }`}>Add New Parameter</h2>
              </div>
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => setShowAddModal(false)}
                  className={`transition-colors focus:outline-none ${
                    darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <FaTimes className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            <form onSubmit={createParameter}>
              <div className="mb-4">
                <label className={`block font-medium mb-2 ${
                  darkMode ? 'text-gray-300' : 'text-gray-700'
                }`} htmlFor="parameterName">
                  Parameter Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="parameterName"
                  name="parameterName"
                  ref={parameterNameInputRef}
                  value={formData.parameterName}
                  onChange={handleInputChange}
                  className={`w-full p-3 border rounded-xl focus:ring-2 focus:ring-blue-500 transition-all ${
                    errors.parameterName 
                      ? 'border-red-500' 
                      : darkMode 
                        ? 'border-gray-600 bg-gray-700 text-gray-200 placeholder-gray-400' 
                        : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
                  }`}
                  autoComplete="off"
                />
                {errors.parameterName && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <FaExclamationCircle className="h-4 w-4" />
                    {errors.parameterName}
                  </p>
                )}
              </div>
              
              <div className="mb-6">
                <label className={`block font-medium mb-2 ${
                  darkMode ? 'text-gray-300' : 'text-gray-700'
                }`} htmlFor="assignedFor">
                  Assigned For <span className="text-red-500">*</span>
                </label>
                <select
                  id="assignedFor"
                  name="assignedFor"
                  value={formData.assignedFor}
                  onChange={handleInputChange}
                  className={`w-full p-3 border rounded-xl focus:ring-2 focus:ring-blue-500 transition-all ${
                    errors.assignedFor 
                      ? 'border-red-500' 
                      : darkMode 
                        ? 'border-gray-600 bg-gray-700 text-gray-200' 
                        : 'border-gray-300 bg-white text-gray-900'
                  }`}
                >
                  <option value="category">Category</option>
                  <option value="type">Type</option>
                  <option value="finish">Finish</option>
                  <option value="mm">Size (MM)</option>
                  <option value="inches">Size (Inches)</option>
                </select>
                {errors.assignedFor && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <FaExclamationCircle className="h-4 w-4" />
                    {errors.assignedFor}
                  </p>
                )}
              </div>
              
              {errors.general && (
                <div className={`mb-4 p-3 border rounded-xl ${
                  darkMode ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'
                }`}>
                  <p className="text-sm text-red-600 flex items-center gap-2">
                    <FaExclamationCircle className="h-4 w-4" />
                    {errors.general}
                  </p>
                </div>
              )}
              
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className={`px-6 py-3 rounded-xl transition-all focus:outline-none focus:ring-2 ${
                    darkMode 
                      ? 'bg-gray-700 text-gray-200 hover:bg-gray-600 focus:ring-gray-500' 
                      : 'bg-gray-300 text-gray-700 hover:bg-gray-400 focus:ring-gray-400'
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className={`px-6 py-3 text-white rounded-xl transition-all flex items-center focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                    submitting ? 'bg-blue-300 cursor-not-allowed' : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl transform hover:scale-105'
                  }`}
                >
                  {submitting && (
                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  <FaCheckCircle className="mr-2" />
                  {submitting ? 'Creating...' : 'Create Parameter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Parameter Modal */}
      {showEditModal && (
        <div 
          className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 p-4"
          onKeyDown={handleModalKeyDown}
        >
          <div className={`p-6 rounded-2xl shadow-2xl w-full max-w-md animate-fade-in-down ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  darkMode ? 'bg-blue-500/20' : 'bg-blue-50'
                }`}>
                  <FaEdit className={`text-xl ${
                    darkMode ? 'text-blue-400' : 'text-blue-600'
                  }`} />
                </div>
                <h2 className={`text-lg font-bold ${
                  darkMode ? 'text-white' : 'text-gray-900'
                }`}>Edit Parameter</h2>
              </div>
              <button 
                onClick={() => setShowEditModal(false)}
                className={`transition-colors focus:outline-none ${
                  darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <FaTimes className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={updateParameter}>
              <div className="mb-4">
                <label className={`block font-medium mb-2 ${
                  darkMode ? 'text-gray-300' : 'text-gray-700'
                }`} htmlFor="edit-parameterName">
                  Parameter Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="edit-parameterName"
                  name="parameterName"
                  ref={parameterNameInputRef}
                  value={formData.parameterName}
                  onChange={handleInputChange}
                  className={`w-full p-3 border rounded-xl focus:ring-2 focus:ring-blue-500 transition-all ${
                    errors.parameterName 
                      ? 'border-red-500' 
                      : darkMode 
                        ? 'border-gray-600 bg-gray-700 text-gray-200 placeholder-gray-400' 
                        : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
                  }`}
                  autoComplete="off"
                />
                {errors.parameterName && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <FaExclamationCircle className="h-4 w-4" />
                    {errors.parameterName}
                  </p>
                )}
              </div>
              
              <div className="mb-6">
                <label className={`block font-medium mb-2 ${
                  darkMode ? 'text-gray-300' : 'text-gray-700'
                }`} htmlFor="edit-assignedFor">
                  Assigned For <span className="text-red-500">*</span>
                </label>
                <select
                  id="edit-assignedFor"
                  name="assignedFor"
                  value={formData.assignedFor}
                  onChange={handleInputChange}
                  className={`w-full p-3 border rounded-xl focus:ring-2 focus:ring-blue-500 transition-all ${
                    errors.assignedFor 
                      ? 'border-red-500' 
                      : darkMode 
                        ? 'border-gray-600 bg-gray-700 text-gray-200' 
                        : 'border-gray-300 bg-white text-gray-900'
                  }`}
                >
                  <option value="category">Category</option>
                  <option value="type">Type</option>
                  <option value="finish">Finish</option>
                  <option value="mm">Size (MM)</option>
                  <option value="inches">Size (Inches)</option>
                </select>
                {errors.assignedFor && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <FaExclamationCircle className="h-4 w-4" />
                    {errors.assignedFor}
                  </p>
                )}
              </div>
              
              {errors.general && (
                <div className={`mb-4 p-3 border rounded-xl ${
                  darkMode ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'
                }`}>
                  <p className="text-sm text-red-600 flex items-center gap-2">
                    <FaExclamationCircle className="h-4 w-4" />
                    {errors.general}
                  </p>
                </div>
              )}
              
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className={`px-6 py-3 rounded-xl transition-all focus:outline-none focus:ring-2 ${
                    darkMode 
                      ? 'bg-gray-700 text-gray-200 hover:bg-gray-600 focus:ring-gray-500' 
                      : 'bg-gray-300 text-gray-700 hover:bg-gray-400 focus:ring-gray-400'
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className={`px-6 py-3 text-white rounded-xl transition-all flex items-center focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                    submitting ? 'bg-blue-300 cursor-not-allowed' : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl transform hover:scale-105'
                  }`}
                >
                  {submitting && (
                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  <FaCheckCircle className="mr-2" />
                  {submitting ? 'Updating...' : 'Update Parameter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Auto-save Notification */}
      {(draftManager.autoSaving || showNotification) && (
        <div
          className={`fixed bottom-6 right-6 bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-4 flex items-center gap-4 max-w-sm border transition-all duration-300 z-50 ${
              draftManager.autoSaving 
                ? 'border-blue-300 dark:border-blue-700' 
                : 'border-green-300 dark:border-green-700'
            }`}
            onClick={() => !draftManager.autoSaving && setShowNotification(false)}
            title={!draftManager.autoSaving ? 'Click to dismiss' : ''}
            style={{ cursor: draftManager.autoSaving ? 'default' : 'pointer' }}
          >
              <div className={`text-2xl ${draftManager.autoSaving ? 'animate-spin' : ''}`}>
                {draftManager.autoSaving ? '🔄' : '✅'}
              </div>
              <div className="flex-1">
                <p className={`font-semibold text-sm ${
                  draftManager.autoSaving 
                    ? 'text-blue-700 dark:text-blue-400' 
                    : 'text-green-700 dark:text-green-400'
                }`}>
                  {draftManager.autoSaving ? 'Auto-saving...' : notificationMessage}
                </p>
                {draftManager.lastSaved && !draftManager.autoSaving && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {draftManager.lastSaved.toLocaleTimeString('en-IN')}
                  </p>
                )}
              </div>
          </div>
      )}
      
      {/* Add CSS for animations */}
      <style jsx global>{`
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-down {
          animation: fadeInDown 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default TilesParameters;
